// apps/admin/src/features/roles/AdminRoleEditorDialog.tsx
//
// Phase 8.2 Wave 7 Task 51 — Admin Role create/edit dialog.
//
// Per CONTEXT.md
//   • Role CRUD with optimistic update + invalidation. Wraps
//                createAdminRole / updateAdminRole helpers from
//                api/admin-roles.api.ts; invalidates adminRoleKeys.lists()
//                on success.
//   • Permission catalog comes from GET /admin/permissions/catalog.
//                If it fails, the dialog shows an error and blocks saving —
//                no client-side fallback list (SEC-5).
//   • PermissionTreeView amount-gate tier selector.
//   • Built-in roles render the editor in READ-ONLY mode; only
//                custom roles support rename + perm diff.
//   • Audit log gets one entry per perm diff line (BE-emitted).
//                FE merely sends the new permission array; BE diffs against
//                the prior set.
//   • Flat colon-notation storage. Tree UI is a render-only
//                transform (parsePermissionTree); the wire format stays flat.
//
// vg-binding: PLAN:task-51
// vg-binding: P8.D-100
// vg-binding: P8.D-132
// vg-binding: P8.D-135
// vg-binding: P8.D-475
// vg-binding: P8.D-490
// vg-binding: P8.D-495

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  createAdminRole,
  getAdminPermissionsCatalog,
  getAdminRole,
  updateAdminRole,
  type AdminPermissionCatalogEntry,
  type AdminRoleDetail,
} from '../../api/admin-roles.api.js';
import { ApiError } from '../../lib/apiClient.js';
import {
  adminRoleKeys,
  invalidateAdminRoleCaches,
} from '../../queries/adminRoleKeys.js';

import { PermissionTreeView } from './PermissionTreeView.js';

// ---------------------------------------------------------------------------
// Zod schema — code + name + description bounded; permissions ≥ 1 colon key.
// Built inside the component (factory) so validation messages localize.
// ---------------------------------------------------------------------------

const COLON_KEY = /^[a-z][a-z0-9_]*(:[a-z][a-z0-9_]*)*$/;

type Translate = (key: string, vars?: Record<string, unknown>) => string;

function buildEditorSchema(t: Translate) {
  return z.object({
    code: z
      .string()
      .min(2, t('role.editor.validation.codeMin'))
      .max(50, t('role.editor.validation.codeMax'))
      .regex(/^[a-z][a-z0-9_]*$/, t('role.editor.validation.codePattern')),
    name: z
      .string()
      .min(2, t('role.editor.validation.nameMin'))
      .max(200, t('role.editor.validation.nameMax')),
    description: z.string().max(2000, t('role.editor.validation.descMax')).optional(),
    permissions: z
      .array(z.string().regex(COLON_KEY))
      .min(1, t('role.editor.validation.permMin')),
  });
}

interface EditorValues {
  code: string;
  name: string;
  description?: string;
  permissions: string[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AdminRoleEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Mongo ObjectId of the role being edited. Pass `null` to render the
   * CREATE form (POST /admin/roles); pass an id to render the EDIT form
   * (GET /admin/roles/:id → PATCH /admin/roles/:id).
   */
  roleId: string | null;
  /**
   * When editing a built-in role, the dialog renders READ-ONLY per
   *  (built-ins are seeded + immutable). Set true to enforce.
   */
  isBuiltIn?: boolean;
  /** Called after a successful create / update. */
  onSaved?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminRoleEditorDialog({
  open,
  onOpenChange,
  roleId,
  isBuiltIn = false,
  onSaved,
}: AdminRoleEditorDialogProps): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('users');
  const queryClient = useQueryClient();
  const isEdit = roleId !== null;
  const readOnly = isBuiltIn;

  const EditorSchema = useMemo(() => buildEditorSchema(t), [t]);

  // ── Fetch permission catalog (BE is source of truth) ───────
  const catalogQuery = useQuery({
    queryKey: adminRoleKeys.catalog(),
    queryFn: () => getAdminPermissionsCatalog(),
    enabled: open,
    staleTime: 5 * 60_000,
    retry: (failureCount, err) => {
      // Don't retry on 404 — the catalog route is missing, retrying won't help.
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  // ── Catalog unavailable → error + save blocked (no client fallback) ─────
  const catalogUnavailable = catalogQuery.isError;
  const catalogData = catalogQuery.data;
  const catalog: AdminPermissionCatalogEntry[] = useMemo(
    () => catalogData ?? [],
    [catalogData],
  );

  // ── Fetch existing role detail (edit mode) ─────────────────────────────
  const detailQuery = useQuery<AdminRoleDetail>({
    queryKey: roleId ? adminRoleKeys.detail(roleId) : ['admin', 'roles', 'detail', '__none__'],
    queryFn: () => {
      if (!roleId) throw new Error('roleId required for detail fetch');
      return getAdminRole(roleId);
    },
    enabled: open && isEdit,
    staleTime: 30_000,
  });

  // ── Form state ─────────────────────────────────────────────────────────
  const [form, setForm] = useState<EditorValues>({
    code: '',
    name: '',
    description: '',
    permissions: [],
  });
  const [errors, setErrors] = useState<Partial<Record<keyof EditorValues | 'submit', string>>>({});

  // Initialise / reset on open + when the detail loads.
  useEffect(() => {
    if (!open) return;
    if (isEdit && detailQuery.data) {
      setForm({
        code: detailQuery.data.code,
        name: detailQuery.data.name,
        description: detailQuery.data.description ?? '',
        permissions: [...detailQuery.data.permissions],
      });
    } else if (!isEdit) {
      setForm({ code: '', name: '', description: '', permissions: [] });
    }
    setErrors({});
  }, [open, isEdit, detailQuery.data]);

  const selectedKeysSet = useMemo(
    () => new Set(form.permissions),
    [form.permissions],
  );

  // ── Per-key label / description maps (consumed by tree) ────────────────
  const labelByKey = useMemo(() => {
    const out: Record<string, string> = {};
    for (const entry of catalog) {
      out[entry.key] = entry.label_vi || entry.label_en || entry.key;
    }
    return out;
  }, [catalog]);
  const descriptionByKey = useMemo(() => {
    const out: Record<string, string> = {};
    for (const entry of catalog) {
      if (entry.description) out[entry.key] = entry.description;
    }
    return out;
  }, [catalog]);

  // ── Toggle handlers ───────────────────────────────────────────────────
  function togglePermission(key: string, checked: boolean): void {
    if (readOnly) return;
    setForm((prev) => {
      const next = new Set(prev.permissions);
      if (checked) next.add(key);
      else next.delete(key);
      return { ...prev, permissions: Array.from(next).sort() };
    });
  }

  function changeAmountTier(action: {
    parentPath: string;
    nextKey: string | null;
    revoke: ReadonlyArray<string>;
  }): void {
    if (readOnly) return;
    setForm((prev) => {
      const next = new Set(prev.permissions);
      // Revoke siblings first (segregation-of-duties enforced).
      for (const k of action.revoke) next.delete(k);
      if (action.nextKey) {
        next.add(action.nextKey);
        // Auto-grant baseline parent for `:unlimited` keys so the
        //  BE 400 never fires. The parent lookup is local
        // we walk the catalog to find the `parent` declaration.
        const entry = catalog.find((e) => e.key === action.nextKey);
        if (entry?.parent) next.add(entry.parent);
      }
      return { ...prev, permissions: Array.from(next).sort() };
    });
  }

  // ── Validation + submit ───────────────────────────────────────────────
  function validate(): EditorValues | null {
    const parsed = EditorSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === 'string') {
          fieldErrors[field as keyof EditorValues] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return null;
    }
    setErrors({});
    return parsed.data;
  }

  const saveMutation = useMutation({
    mutationFn: async (
      values: EditorValues,
    ): Promise<{ granted: number; revoked: number }> => {
      // Permission diff vs the role's current server-side set (detailQuery is
      // the pre-edit baseline). Surfaced in the success toast so the admin
      // sees exactly how many grants/revokes were applied (issue #1072).
      const baseline = new Set(detailQuery.data?.permissions ?? []);
      const nextSet = new Set(values.permissions);
      const granted = values.permissions.filter((k) => !baseline.has(k)).length;
      const revoked = [...baseline].filter((k) => !nextSet.has(k)).length;
      if (isEdit && roleId) {
        await updateAdminRole(roleId, {
          name: values.name,
          description: values.description ?? null,
          permissions: values.permissions,
        });
      } else {
        await createAdminRole({
          code: values.code,
          name: values.name,
          ...(values.description ? { description: values.description } : {}),
          permissions: values.permissions,
        });
      }
      return { granted, revoked };
    },
    onSuccess: ({ granted, revoked }) => {
      toast({
        title: isEdit ? t('role.editor.toast.savedTitle') : t('role.editor.toast.createdTitle'),
        description: isEdit
          ? t('role.editor.toast.savedDescCount', {
              name: form.name,
              granted,
              revoked,
            })
          : t('role.editor.toast.createdDesc', { name: form.name }),
        variant: 'success',
      });
      invalidateAdminRoleCaches(queryClient, roleId ?? undefined);
      onSaved?.();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('role.editor.toast.saveFail');
      setErrors((prev) => ({ ...prev, submit: msg }));
    },
  });

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (readOnly) return;
    const values = validate();
    if (!values) return;
    await saveMutation.mutateAsync(values);
  }

  // ── Render ────────────────────────────────────────────────────────────
  const submitting = saveMutation.isPending;
  const catalogLoading = catalogQuery.isLoading;
  const detailLoading = isEdit && detailQuery.isLoading;
  const nameOrLoading = form.name || t('common.loading');
  const headerTitle = readOnly
    ? t('role.editor.titleView', { name: nameOrLoading })
    : isEdit
      ? t('role.editor.titleEdit', { name: nameOrLoading })
      : t('role.editor.titleCreate');

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      className="max-w-3xl max-h-[90vh] overflow-y-auto"
    >
      <DialogHeader>
        <DialogTitle>{headerTitle}</DialogTitle>
      </DialogHeader>

      {readOnly ? (
        <div className="mt-3 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-3 text-sm text-blue-900">
          {t('role.editor.builtInBanner')}
        </div>
      ) : null}

      {catalogUnavailable ? (
        <div
          role="alert"
          className="mt-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900"
        >
          {t('role.editor.catalogLoadError')}
        </div>
      ) : null}

      {catalogLoading || detailLoading ? (
        <div className="my-6 text-center text-sm text-pw-text-muted">
          {t('role.editor.loadingCatalog')}
        </div>
      ) : (
        <Form
          onSubmit={handleSubmit}
          className="mt-4 flex flex-col gap-4"
          data-testid="admin-roles-editor-form"
        >
          {!isEdit ? (
            <FormField>
              <FormLabel htmlFor="admin-roles-code-input">
                {t('role.editor.fields.code')}
              </FormLabel>
              <FormControl>
                <Input
                  id="admin-roles-code-input"
                  data-testid="admin-roles-code-input"
                  value={form.code}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, code: e.target.value }))
                  }
                  placeholder={t('role.editor.placeholders.code')}
                  disabled={readOnly}
                  maxLength={50}
                />
              </FormControl>
              {errors.code && <FormMessage>{errors.code}</FormMessage>}
            </FormField>
          ) : null}

          <FormField>
            <FormLabel htmlFor="admin-roles-name-input">{t('role.editor.fields.name')}</FormLabel>
            <FormControl>
              <Input
                id="admin-roles-name-input"
                data-testid="admin-roles-name-input"
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder={t('role.editor.placeholders.name')}
                disabled={readOnly}
                maxLength={200}
              />
            </FormControl>
            {errors.name && <FormMessage>{errors.name}</FormMessage>}
          </FormField>

          <FormField>
            <FormLabel htmlFor="admin-roles-description-input">
              {t('role.editor.fields.description')}
            </FormLabel>
            <FormControl>
              <Input
                id="admin-roles-description-input"
                data-testid="admin-roles-description-input"
                value={form.description ?? ''}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
                placeholder={t('role.editor.placeholders.description')}
                disabled={readOnly}
                maxLength={2000}
              />
            </FormControl>
            {errors.description && (
              <FormMessage>{errors.description}</FormMessage>
            )}
          </FormField>

          <fieldset
            className="rounded-md border border-pw-border p-3"
            data-testid="admin-roles-tab-data-permissions"
          >
            <legend className="px-1 text-sm font-medium">
              {t('role.editor.permissionsLegend')}
            </legend>
            <p className="mb-3 text-xs text-pw-text-muted">
              {t('role.editor.permissionsHelp')}
            </p>
            <PermissionTreeView
              catalogKeys={catalog.map((c) => c.key)}
              selectedKeys={selectedKeysSet}
              labelByKey={labelByKey}
              descriptionByKey={descriptionByKey}
              readOnly={readOnly}
              onTogglePermission={togglePermission}
              onChangeAmountTier={changeAmountTier}
            />
            {errors.permissions && (
              <FormMessage>{errors.permissions}</FormMessage>
            )}
            <p className="mt-3 text-xs text-pw-text-muted">
              {t('role.editor.selectedCountPrefix')}{' '}
              <strong>{form.permissions.length}</strong>{' '}
              {t('role.editor.selectedCountSuffix')}
            </p>
          </fieldset>

          {errors.submit ? (
            <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-900">
              {errors.submit}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {readOnly ? t('common.close') : t('common.cancel')}
            </Button>
            {!readOnly ? (
              <Button
                type="submit"
                variant="default"
                disabled={submitting || catalogUnavailable}
                data-testid="admin-roles-save-btn"
              >
                {submitting
                  ? t('common.saving')
                  : isEdit
                    ? t('common.save')
                    : t('role.editor.createBtn')}
              </Button>
            ) : null}
          </DialogFooter>
        </Form>
      )}
    </Dialog>
  );
}

export default AdminRoleEditorDialog;
